import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketplaceTopNavigation from '@/components/MarketplaceTopNavigation';
import BottomNavigation from '@/components/BottomNavigation';
import { useCart } from '@/contexts/CartContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { calculateCartTotalWithBundles } from '@/utils/salePricing';

const MarketplaceCartPage = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, total, itemCount, refreshCart, setCartType } = useCart();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCartType('marketplace'); // Set cart to marketplace mode
    const load = async () => {
      await refreshCart();
      setLoading(false);
    };
    load();
  }, [refreshCart, setCartType]);

  // Calculate totals with Frequence bundle discount
  const cartTotals = useMemo(() => {
    return calculateCartTotalWithBundles(items.map(item => ({
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      restaurant_name: item.restaurant_name
    })));
  }, [items]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <MarketplaceTopNavigation />
      <div className="px-4 py-4">
        <h1 className="text-xl font-semibold mb-4">Marketplace Cart ({itemCount} items)</h1>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-2">🧺</div>
            <p className="text-muted-foreground">Your marketplace cart is empty</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-4 flex gap-3 items-center">
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-md object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{item.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">Qty: {item.quantity}</p>
                    <p className="font-semibold mt-1">D{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</Button>
                    <Button variant="outline" size="sm" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</Button>
                    <Button variant="outline" size="sm" onClick={() => removeItem(item.id)}>Remove</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-20 left-0 right-0 bg-card border-t border-border p-4">
          {cartTotals.bundleDiscount > 0 && (
            <div className="mb-2 p-2 bg-green-500/10 rounded-lg">
              <p className="text-sm text-green-600 font-medium">
                🎉 Frequence Bundle Deal: {cartTotals.frequenceCount} items - Save D{cartTotals.bundleDiscount.toFixed(0)}!
              </p>
            </div>
          )}
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-muted-foreground">Subtotal:</span>
            <span className={`text-sm ${cartTotals.bundleDiscount > 0 ? 'line-through text-muted-foreground' : 'font-semibold'}`}>
              D{cartTotals.subtotal.toFixed(2)}
            </span>
          </div>
          {cartTotals.bundleDiscount > 0 && (
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-green-600">Bundle Discount:</span>
              <span className="text-sm text-green-600">-D{cartTotals.bundleDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-3">
            <span className="text-lg font-semibold">Total:</span>
            <span className="text-xl font-bold text-primary">D{cartTotals.total.toFixed(2)}</span>
          </div>
          <Button className="w-full" onClick={() => navigate('/checkout')}>
            Proceed to Checkout
          </Button>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default MarketplaceCartPage;
